import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  PLATFORM_ID,
  ViewChild,
  signal,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  SideBySideCarouselAndDynamicText,
  SideBySideCarouselDynamicTextItem,
} from 'www-cms';
import { BaseBuildingBlockComponent } from '../components/base-building-block.component';
import { LastItemBgClass } from '../../shell/shell.service';
import { ImageWithLoaderComponent } from '../components/image-with-loader/image-with-loader.component';
import { CarouselControlsComponent } from '../../shared/components/carousel-controls/carousel-controls.component';
import { PlayButtonOverlayComponent } from '../components/play-button-overlay/play-button-overlay.component';
import { BehaviorSubject } from 'rxjs';
import Player from '@vimeo/player';
import YoutubePlayer from 'youtube-player';
import { YouTubePlayer } from 'youtube-player/dist/types';
import type { VimeoEmbedParameters } from '@vimeo/player/types/formats';
import { CookieConsentService } from 'cookie-consent/lib/services/cookie-consent.service';
import { RouterLink } from '@angular/router';
import { DirectusDirective } from 'directus';

@Component({
  selector: 'ss-www-side-by-side-carousel-dynamic-text',
  imports: [
    CommonModule,
    ImageWithLoaderComponent,
    CarouselControlsComponent,
    PlayButtonOverlayComponent,
    RouterLink,
    DirectusDirective,
  ],
  templateUrl: './side-by-side-carousel-dynamic-text.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideBySideCarouselDynamicTextComponent
  extends BaseBuildingBlockComponent
  implements AfterViewInit
{
  private platformId = inject(PLATFORM_ID);
  private readonly consentService = inject(CookieConsentService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({ required: true })
  sideBySideCarouselAndDynamicTextData!: SideBySideCarouselAndDynamicText;
  currentIndex = signal<number>(0);
  currentText = signal<string>('');
  private player?: Player | YouTubePlayer;
  youTubePoster?: HTMLImageElement;
  @ViewChild('video') video?: ElementRef;
  playClicked$ = new BehaviorSubject(false);

  override getBgClass(): LastItemBgClass {
    return this.sideBySideCarouselAndDynamicTextData.theme === 'light'
      ? 'bg-white'
      : 'bg-ss-www-background';
  }
  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (
      this.sideBySideCarouselAndDynamicTextData?.items[this.currentIndex()]
        .video
    ) {
      this.initializeVideo();
    }
  }

  private initializeVideo(): void {
    const item =
      this.sideBySideCarouselAndDynamicTextData?.items[this.currentIndex()];
    if (!isPlatformBrowser(this.platformId) || !item?.video || !this.video) {
      return;
    }
    if (item.videoProvider === 'vimeo' && !item.customVideoThumbnail) {
      const config: VimeoEmbedParameters = {
        id: parseInt(item.video),
        autoplay: false,
        controls: false,
        responsive: true,
        background: false,
        dnt: this.consentService.consent().performance,
      };
      this.player = new Player(this.video.nativeElement, config);
    } else if (item.videoProvider === 'youtube' && !item.customVideoThumbnail) {
      // Do not create the YouTube player on init. Instead use poster to avoid red play button inside the player UI
      const container = this.video?.nativeElement as HTMLElement | undefined;
      if (container) {
        const img = document.createElement('img');
        img.src = `https://i.ytimg.com/vi/${encodeURIComponent(item.video)}/hqdefault.jpg`;
        img.alt = 'Video poster';
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.loading = 'eager';
        img.decoding = 'async';
        this.youTubePoster = img;
        container.appendChild(img);
      }
    }
  }

  private resetPlayState(): void {
    this.playClicked$.next(false);
    if (this.player) {
      this.player.destroy();
      this.player = undefined;
    }
    if (this.youTubePoster) {
      this.youTubePoster.remove();
      this.youTubePoster = undefined;
    }
    if (this.video?.nativeElement) {
      this.video.nativeElement.innerHTML = '';
    }
  }

  imgTackBy(index: number, item: SideBySideCarouselDynamicTextItem) {
    return item.image?.id ?? item.video;
  }

  bigPlayButtonClicked() {
    const item =
      this.sideBySideCarouselAndDynamicTextData?.items[this.currentIndex()];
    this.playClicked$.next(true);
    // If there's no video id configured, do nothing
    if (!isPlatformBrowser(this.platformId) || !item?.video || !this.video) {
      return;
    }

    this.playClicked$.next(true);
    if (this.player) {
      this.player.destroy();
    }

    // Handle provider-specific creation
    if (item.videoProvider === 'vimeo') {
      const config: VimeoEmbedParameters = {
        id: parseInt(item.video),
        autoplay: false,
        controls: true,
        responsive: true,
        background: false,
        dnt: this.consentService.consent().performance,
      };
      this.player = new Player(this.video.nativeElement, config);
      this.player.play();
    } else if (item.videoProvider === 'youtube') {
      // Remove poster image if present
      if (this.youTubePoster) {
        this.youTubePoster.remove();
      }

      // create the youtube-player and start playback
      this.player = YoutubePlayer(this.video?.nativeElement, {
        videoId: item.video,
        playerVars: {
          autoplay: 0,
          controls: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
        },
        host: 'https://www.youtube-nocookie.com',
      });
      this.player.playVideo();
    }
  }
  setIndex(index: number) {
    this.resetPlayState();
    this.currentIndex.set(index);
    this.currentText.set(
      this.sideBySideCarouselAndDynamicTextData.items[index].textContent
    );
    if (
      this.sideBySideCarouselAndDynamicTextData.items[this.currentIndex()].video
    ) {
      this.cdr.detectChanges();
      this.initializeVideo();
    }
  }
}
